import { Close } from '@mui/icons-material'
import { alpha, Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Radio, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { useState } from 'react'
import { IClassifiedFeature } from '../../../../definitions/Classification'
import { IFormField } from '../../../../definitions/FormField'
import VectorIcon from '../../../icons/VectorIcon'

interface DuplicateFeature {
    feature: IClassifiedFeature
    field: IFormField | undefined
}

interface P {
    open: boolean
    onClose: () => void
    duplicateFeatures: DuplicateFeature[]
    onConfirm: (resolvedFeatures: IClassifiedFeature[]) => void
}

const ContractDuplicateResolutionModal = ({ open, onClose, duplicateFeatures, onConfirm }: P) => {
    const theme = useTheme()

    const filteredFeatures = duplicateFeatures.filter(f => f?.feature?.alternatives?.length > 0)

    const [selections, setSelections] = useState<Record<string, number>>(() => {
        const initial: Record<string, number> = {}
        duplicateFeatures.forEach(({ feature }) => {
            initial[feature.name] = -1
        })
        return initial
    })

    const handleSelect = (featureName: string, altIndex: number) => {
        setSelections(prev => ({ ...prev, [featureName]: altIndex }))
    }

    const handleConfirm = () => {
        const resolved = duplicateFeatures.map(({ feature }) => {
            const selectedIndex = selections[feature.name] ?? -1
            if (selectedIndex === -1) return feature

            const selectedAlt = feature.alternatives[selectedIndex]
            const updatedAlternatives = feature.alternatives.map((alt, i) =>
                i === selectedIndex
                    ? { value: feature.value, confidence: feature.confidence, source: feature.source }
                    : alt
            )
            return {
                ...feature,
                value: selectedAlt.value,
                confidence: selectedAlt.confidence,
                source: selectedAlt.source,
                alternatives: updatedAlternatives
            }
        })
        onConfirm(resolved)
    }

    const getConfidenceColor = (confidence: number) => {
        const red = Math.max(0, 255 - Math.floor(confidence * 225))
        const green = Math.min(255, Math.floor(confidence * 255))
        return `rgb(${red}, ${green}, 50)`
    }

    const renderValue = (value: unknown) => {
        if (typeof value === 'object' && value !== null) {
            return (
                <pre style={{ margin: 0, fontSize: 12 }}>
                    {JSON.stringify(value, null, 2)}
                </pre>
            )
        }
        return <Typography variant='body2'>{String(value)}</Typography>
    }

    const allResolved = filteredFeatures.every(({ feature }) => selections[feature.name] !== undefined)

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='md'
            fullWidth
            PaperProps={{ sx: { borderRadius: 2, minHeight: 500 } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Box display='flex' alignItems='center' gap={1}>
                        <VectorIcon />
                        <Typography variant='h6'>Resolve Duplicate Values</Typography>
                    </Box>
                    <IconButton onClick={onClose} size='small'>
                        <Close />
                    </IconButton>
                </Box>
                <Typography variant='body2' color='textSecondary' mt={0.5}>
                    The AI found multiple possible values for the following fields. Select the value you want to keep for each.
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pb: 2 }}>
                <Stack spacing={2}>
                    {filteredFeatures.map(({ feature, field }) => {
                        const selectedIndex = selections[feature.name] ?? -1
                        const displayLabel = field?.label || feature.name
                        const isObjectType = feature.alternatives.some(
                            alt => typeof alt.value === 'object' && alt.value !== null
                        )
                        return (
                            <Card key={feature.name} variant='outlined' sx={{ p: 2 }}>
                                <Typography variant='subtitle2' fontWeight='bold' mb={1}>
                                    {displayLabel}
                                </Typography>

                                <Stack sx={{ width: '100%' }} spacing={1} direction={isObjectType ? 'row' : 'column'}>

                                    {/* Alternative values */}
                                    {feature.alternatives.map((alt, altIndex) => {
                                        return (
                                            <Box
                                                key={altIndex}
                                                onClick={() => handleSelect(feature.name, altIndex)}
                                                sx={{
                                                    'width': isObjectType ? '50%' : '100%',
                                                    'flex': isObjectType ? 1 : 'undefined',
                                                    'display': 'flex',
                                                    'alignItems': 'center',
                                                    'gap': 1,
                                                    'p': 1,
                                                    'borderRadius': 1,
                                                    'border': '1px solid',
                                                    'borderColor': selectedIndex === altIndex
                                                        ? theme.palette.primary.main
                                                        : theme.palette.divider,
                                                    'backgroundColor': selectedIndex === altIndex
                                                        ? alpha(theme.palette.primary.main, 0.06)
                                                        : 'transparent',
                                                    'cursor': 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                                    }
                                                }}>
                                                <Radio
                                                    checked={selectedIndex === altIndex}
                                                    size='small'
                                                />
                                                <Box flex={1} sx={{ overflow: 'auto', maxHeight: 150 }}>
                                                    {renderValue(alt.value)}
                                                </Box>
                                                <Tooltip title={alt.source || ''}>
                                                    <Typography
                                                        variant='caption'
                                                        sx={{ color: getConfidenceColor(alt.confidence) }}>
                                                        {(alt.confidence * 100).toFixed(0)}% confidence
                                                    </Typography>
                                                </Tooltip>
                                            </Box>
                                        )
                                    })}
                                </Stack>
                            </Card>
                        )
                    })}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant='outlined' onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant='contained'
                    disabled={!allResolved}
                    onClick={handleConfirm}>
                    Confirm Selection
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export type { DuplicateFeature }
export default ContractDuplicateResolutionModal
